// frontend/src/pages/AnalyticsPage.jsx
/**
 * AnalyticsPage — advanced analytics hub for the active organisation.
 *
 * Sections:
 *   1. Date range picker   — Last 7 / 30 / 90 days, or Custom (date inputs).
 *   2. Comparison toggle   — overlay previous-period data + delta badges.
 *   3. KPI cards           — scans, unique visitors, active codes (+ delta).
 *   4. Scans by day        — LineChart; two series when comparison is on.
 *   5. Device split        — DeviceDonut pie chart.
 *   6. Country breakdown   — CountryBars horizontal bar chart.
 *   7. Hour heatmap        — 24-cell intensity grid (scans by hour of day).
 *   8. UTM breakdown       — three tabs: Sources / Mediums / Campaigns.
 *   9. CSV export          — date-range export button; gated on Pro+ plan.
 *  10. Scheduled reports   — configure / save / disable a recurring email report.
 */
import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { format, parseISO, subDays } from "date-fns";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import toast from "react-hot-toast";

import { useAuth } from "../context/AuthContext";
import {
  getDashboardSummary,
  getUTMBreakdown,
  exportOrgScans,
  getScheduledReport,
  upsertScheduledReport,
  deleteScheduledReport,
} from "../api/analytics";
import { getUsage } from "../api/orgs";
import ErrorBanner from "../components/ErrorBanner";
import FeatureGate from "../components/FeatureGate";
import ChartCard from "../components/charts/ChartCard";
import DeviceDonut from "../components/charts/DeviceDonut";
import CountryBars from "../components/charts/CountryBars";

// --------------------------------------------------------------------------- //
// Constants                                                                    //
// --------------------------------------------------------------------------- //

const RANGE_OPTIONS = [
  { label: "7 days", value: "7" },
  { label: "30 days", value: "30" },
  { label: "90 days", value: "90" },
  { label: "Custom", value: "custom" },
];

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// ISO date string for today: YYYY-MM-DD
function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

// ISO date string for N days ago
function daysAgoStr(n) {
  return subDays(new Date(), n).toISOString().slice(0, 10);
}

// --------------------------------------------------------------------------- //
// Small helpers                                                                //
// --------------------------------------------------------------------------- //

function formatHour(h) {
  if (h === 0) return "12am";
  if (h === 12) return "12pm";
  return h < 12 ? `${h}am` : `${h - 12}pm`;
}

/** +12.5% ↑ (green) or -3.2% ↓ (red) badge. */
function DeltaBadge({ pct }) {
  if (pct == null) return null;
  const up = pct >= 0;
  const label = `${up ? "+" : ""}${pct.toFixed(1)}% ${up ? "↑" : "↓"}`;
  return (
    <span
      className={`inline-block text-xs font-semibold px-1.5 py-0.5 rounded ${
        up
          ? "bg-green-50 text-green-700"
          : "bg-red-50 text-red-700"
      }`}
    >
      {label}
    </span>
  );
}
DeltaBadge.propTypes = { pct: PropTypes.number };

/** Skeleton pulse block. */
function Skeleton({ className }) {
  return (
    <div className={`bg-gray-100 rounded-xl animate-pulse ${className}`} />
  );
}
Skeleton.propTypes = { className: PropTypes.string };

// --------------------------------------------------------------------------- //
// KPI card                                                                     //
// --------------------------------------------------------------------------- //

function StatCard({ label, value, delta }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-1.5">
      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
        {label}
      </span>
      <span className="text-3xl font-bold text-gray-900">
        {typeof value === "number" ? value.toLocaleString() : "—"}
      </span>
      {delta != null && <DeltaBadge pct={delta} />}
    </div>
  );
}
StatCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number,
  delta: PropTypes.number,
};

// --------------------------------------------------------------------------- //
// Scans-by-day chart (supports optional comparison second line)               //
// --------------------------------------------------------------------------- //

function ScansByDayDualChart({ currSeries, prevSeries, compare }) {
  const data = currSeries.map((d, i) => ({
    label: format(parseISO(d.date), "d MMM"),
    current: d.count,
    ...(compare && { previous: prevSeries[i]?.count ?? 0 }),
  }));

  const isEmpty = data.length === 0 || data.every((d) => d.current === 0);

  return (
    <ChartCard
      title={compare ? "Scans by day — current vs previous period" : "Scans by day"}
      isEmpty={isEmpty}
    >
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={data} margin={{ top: 8, right: 24, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: "#6b7280" }}
            tickLine={false}
            axisLine={false}
            minTickGap={24}
          />
          <YAxis
            allowDecimals={false}
            tick={{ fontSize: 11, fill: "#6b7280" }}
            tickLine={false}
            axisLine={false}
            width={32}
          />
          <Tooltip />
          <Line
            type="monotone"
            dataKey="current"
            name="Current"
            stroke="#2563eb"
            strokeWidth={2}
            dot={false}
          />
          {compare && (
            <Line
              type="monotone"
              dataKey="previous"
              name="Previous"
              stroke="#94a3b8"
              strokeWidth={2}
              strokeDasharray="5 3"
              dot={false}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
ScansByDayDualChart.propTypes = {
  currSeries: PropTypes.array.isRequired,
  prevSeries: PropTypes.array,
  compare: PropTypes.bool,
};
ScansByDayDualChart.defaultProps = { prevSeries: [], compare: false };

// --------------------------------------------------------------------------- //
// Hour heatmap                                                                 //
// --------------------------------------------------------------------------- //

function HourHeatmap({ data }) {
  const maxCount = Math.max(1, ...data.map((d) => d.count));
  // Ensure all 24 hours are present (fill gaps with 0).
  const cells = Array.from({ length: 24 }, (_, h) => {
    const found = data.find((d) => d.hour === h);
    return { hour: h, count: found ? found.count : 0 };
  });

  return (
    <ChartCard title="Scans by hour of day" isEmpty={maxCount === 1 && data.length === 0}>
      <div className="grid grid-cols-12 gap-1 pt-1">
        {cells.map(({ hour, count }) => {
          const intensity = count / maxCount;
          const bg = intensity === 0
            ? "#f1f5f9"
            : `rgba(37, 99, 235, ${(0.12 + intensity * 0.88).toFixed(2)})`;
          const textColor = intensity > 0.55 ? "#fff" : "#374151";
          return (
            <div
              key={hour}
              title={`${formatHour(hour)}: ${count} scan${count !== 1 ? "s" : ""}`}
              className="rounded flex flex-col items-center justify-center py-2 cursor-default select-none"
              style={{ backgroundColor: bg }}
            >
              <span className="text-[10px] font-medium leading-tight" style={{ color: textColor }}>
                {formatHour(hour)}
              </span>
              {count > 0 && (
                <span className="text-[10px] leading-tight" style={{ color: textColor }}>
                  {count}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </ChartCard>
  );
}
HourHeatmap.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({ hour: PropTypes.number, count: PropTypes.number })
  ).isRequired,
};

// --------------------------------------------------------------------------- //
// UTM breakdown (tabs)                                                         //
// --------------------------------------------------------------------------- //

function UTMRankedList({ rows, keyProp, label }) {
  const maxCount = Math.max(1, ...rows.map((r) => r.count));
  if (rows.length === 0) {
    return (
      <p className="text-sm text-gray-400 py-4 text-center">No UTM data yet.</p>
    );
  }
  return (
    <ul className="space-y-2 pt-1">
      {rows.map((row) => (
        <li key={row[keyProp]} className="flex items-center gap-3">
          <span
            className="text-sm text-gray-700 font-medium truncate"
            style={{ minWidth: "6rem", maxWidth: "10rem" }}
            title={row[keyProp]}
          >
            {row[keyProp]}
          </span>
          <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-brand-500 rounded-full"
              style={{ width: `${(row.count / maxCount) * 100}%` }}
            />
          </div>
          <span className="text-xs text-gray-500 whitespace-nowrap w-10 text-right">
            {label ? `${row.count} ${label}` : row.count}
          </span>
        </li>
      ))}
    </ul>
  );
}
UTMRankedList.propTypes = {
  rows: PropTypes.array.isRequired,
  keyProp: PropTypes.string.isRequired,
  label: PropTypes.string,
};

const UTM_TABS = [
  { id: "sources", label: "Sources", keyProp: "utm_source" },
  { id: "mediums", label: "Mediums", keyProp: "utm_medium" },
  { id: "campaigns", label: "Campaigns", keyProp: "utm_campaign" },
];

function UTMSection({ utm }) {
  const [tab, setTab] = useState("sources");
  const active = UTM_TABS.find((t) => t.id === tab);
  const rows = utm ? (utm[tab] ?? []) : [];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <h2 className="text-sm font-semibold text-gray-700 mb-3">UTM Breakdown</h2>
      <div className="flex gap-1 mb-4 border-b border-gray-100">
        {UTM_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 text-sm font-medium transition-colors rounded-t ${
              tab === t.id
                ? "bg-brand-50 text-brand-700 border-b-2 border-brand-600"
                : "text-gray-500 hover:text-gray-800"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {utm == null ? (
        <p className="text-sm text-gray-400 py-4 text-center">Loading…</p>
      ) : (
        <UTMRankedList rows={rows} keyProp={active.keyProp} />
      )}
    </div>
  );
}
UTMSection.propTypes = { utm: PropTypes.object };

// --------------------------------------------------------------------------- //
// Export button                                                                //
// --------------------------------------------------------------------------- //

function ExportButton({ fromDate, toDate }) {
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportOrgScans(fromDate, toDate);
      toast.success("Export started — check your downloads.");
    } catch {
      // 402 is already handled by the global plan-limit interceptor.
      toast.error("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleExport}
      disabled={exporting}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-60 transition-colors"
    >
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
      </svg>
      {exporting ? "Exporting…" : "Export CSV"}
    </button>
  );
}
ExportButton.propTypes = {
  fromDate: PropTypes.string.isRequired,
  toDate: PropTypes.string.isRequired,
};

/**
 * Static sample of an export, shown blurred behind the FeatureGate preview so
 * sub-Pro users see *what* they'd unlock rather than a dead grey button (PA3-T4).
 */
function ExportPreviewSample() {
  const rows = [
    ["2026-06-21", "promo24", "US", "mobile", 142],
    ["2026-06-21", "flyer-a", "GB", "desktop", 87],
    ["2026-06-20", "menu-qr", "DE", "tablet", 51],
  ];
  return (
    <table className="w-full text-left text-xs">
      <thead className="text-gray-500">
        <tr>
          {["Date", "Code", "Country", "Device", "Scans"].map((h) => (
            <th key={h} className="px-2 py-1 font-semibold">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody className="text-gray-700">
        {rows.map((r) => (
          <tr key={r[1]} className="border-t border-gray-100">
            {r.map((c, i) => (
              <td key={i} className="px-2 py-1 tabular-nums">{c}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// --------------------------------------------------------------------------- //
// Scheduled report section                                                     //
// --------------------------------------------------------------------------- //

function ScheduledReportSection() {
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [frequency, setFrequency] = useState("weekly");
  const [dayOfWeek, setDayOfWeek] = useState(0);
  const [dayOfMonth, setDayOfMonth] = useState(1);

  const load = useCallback(() => {
    getScheduledReport()
      .then((data) => {
        setSchedule(data);
        if (data) {
          setFrequency(data.frequency);
          if (data.day_of_week != null) setDayOfWeek(data.day_of_week);
          if (data.day_of_month != null) setDayOfMonth(data.day_of_month);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    const body = { frequency };
    if (frequency === "weekly") body.day_of_week = dayOfWeek;
    else body.day_of_month = dayOfMonth;
    try {
      const saved = await upsertScheduledReport(body);
      setSchedule(saved);
      toast.success("Report schedule saved.");
    } catch {
      toast.error("Failed to save schedule.");
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = async () => {
    setSaving(true);
    try {
      await deleteScheduledReport();
      setSchedule(null);
      toast.success("Report schedule removed.");
    } catch {
      toast.error("Failed to remove schedule.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <h2 className="text-sm font-semibold text-gray-700 mb-1">Scheduled Report</h2>
      <p className="text-xs text-gray-500 mb-4">
        Receive a periodic analytics email for this organisation.
      </p>

      {loading ? (
        <div className="space-y-2 animate-pulse">
          <div className="h-4 bg-gray-100 rounded w-48" />
          <div className="h-8 bg-gray-100 rounded w-full" />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Last sent */}
          {schedule?.last_sent_at && (
            <p className="text-xs text-gray-500">
              Last sent:{" "}
              <span className="font-medium text-gray-700">
                {format(parseISO(schedule.last_sent_at), "d MMM yyyy, HH:mm")} UTC
              </span>
            </p>
          )}

          {/* Frequency */}
          <div className="flex gap-4">
            {["weekly", "monthly"].map((f) => (
              <label key={f} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="frequency"
                  value={f}
                  checked={frequency === f}
                  onChange={() => setFrequency(f)}
                  className="accent-brand-600"
                />
                <span className="text-sm text-gray-700 capitalize">{f}</span>
              </label>
            ))}
          </div>

          {/* Day selector */}
          {frequency === "weekly" ? (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Day of week</label>
              <select
                value={dayOfWeek}
                onChange={(e) => setDayOfWeek(parseInt(e.target.value, 10))}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-brand-500 focus:outline-none"
              >
                {DAY_NAMES.map((d, i) => (
                  <option key={d} value={i}>{d}</option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Day of month</label>
              <select
                value={dayOfMonth}
                onChange={(e) => setDayOfMonth(parseInt(e.target.value, 10))}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-brand-500 focus:outline-none"
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-1.5 text-sm font-medium bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-60 transition-colors"
            >
              {saving ? "Saving…" : "Save Schedule"}
            </button>
            {schedule && (
              <button
                type="button"
                onClick={handleDisable}
                disabled={saving}
                className="px-4 py-1.5 text-sm font-medium bg-white text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-60 transition-colors"
              >
                Disable
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Page skeleton                                                                //
// --------------------------------------------------------------------------- //

function PageSkeleton() {
  return (
    <div role="status" aria-label="Loading analytics" className="max-w-6xl mx-auto space-y-6 animate-pulse">
      <div className="h-8 w-56 bg-gray-200 rounded" />
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {[0, 1, 2].map((i) => <div key={i} className="h-24 bg-gray-100 rounded-xl border border-gray-200" />)}
      </div>
      <div className="h-64 bg-gray-100 rounded-xl border border-gray-200" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="h-64 bg-gray-100 rounded-xl border border-gray-200" />
        <div className="h-64 bg-gray-100 rounded-xl border border-gray-200" />
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Main page                                                                    //
// --------------------------------------------------------------------------- //

/**
 * AnalyticsPage — top-level analytics hub.
 *
 * State machine:
 *   range ∈ { "7" | "30" | "90" | "custom" }
 *   compare: boolean — when true, fetches a 2× window + delta badges
 */
function AnalyticsPage() {
  // --- date range state ---
  const [range, setRange] = useState("30");
  const [customFrom, setCustomFrom] = useState(daysAgoStr(29));
  const [customTo, setCustomTo] = useState(todayStr());
  const [compare, setCompare] = useState(false);

  // --- data state ---
  const [summary, setSummary] = useState(null);
  const [extendedSummary, setExtendedSummary] = useState(null); // 2× window for prev-period chart
  const [utm, setUtm] = useState(null);
  const [planName, setPlanName] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- derived values ---
  const days =
    range === "custom"
      ? Math.min(
          365,
          Math.max(
            1,
            Math.round(
              (new Date(customTo) - new Date(customFrom)) / 86_400_000
            ) + 1
          )
        )
      : parseInt(range, 10);

  // fromDate / toDate for the export button
  const fromDate = range === "custom" ? customFrom : daysAgoStr(days - 1);
  const toDate = range === "custom" ? customTo : todayStr();

  const canExport = planName === "Pro" || planName === "Business";

  // --- fetch ---
  const load = useCallback(() => {
    setLoading(true);
    setError(null);

    const fetches = [
      getDashboardSummary(days, compare),
      getUTMBreakdown(days),
      compare ? getDashboardSummary(Math.min(days * 2, 365)) : Promise.resolve(null),
    ];

    Promise.all(fetches)
      .then(([sum, utmData, ext]) => {
        setSummary(sum);
        setUtm(utmData);
        setExtendedSummary(ext);
      })
      .catch(() => setError("Failed to load analytics. Please try again."))
      .finally(() => setLoading(false));
  }, [days, compare]);

  useEffect(() => { load(); }, [load]);

  // Fetch plan name once on mount (for export gate).
  useEffect(() => {
    getUsage()
      .then((u) => setPlanName(u?.plan?.name ?? null))
      .catch(() => {});
  }, []);

  // --- derived chart data ---
  const currSeries = summary?.scans_by_day ?? [];
  const prevSeries = extendedSummary
    ? extendedSummary.scans_by_day.slice(0, days)
    : [];

  const kpi = summary?.summary ?? {};
  const compBlock = summary?.comparison ?? null;

  // Sum scans_by_day for an accurate "scans in window" KPI regardless of days.
  const scansInPeriod = currSeries.reduce((s, d) => s + d.count, 0);

  // --------------------------------------------------------------------------- //
  // Render                                                                       //
  // --------------------------------------------------------------------------- //

  if (loading && !summary) {
    return <PageSkeleton />;
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto py-8">
        <ErrorBanner message={error} onRetry={load} />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 flex-wrap">
        <h1 className="text-xl font-semibold text-gray-900">Analytics</h1>

        <div className="flex flex-wrap items-center gap-3">
          {/* Date range picker */}
          <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
            {RANGE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setRange(opt.value)}
                aria-pressed={range === opt.value}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  range === opt.value
                    ? "bg-brand-600 text-white"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Comparison toggle */}
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={compare}
              onChange={(e) => setCompare(e.target.checked)}
              className="accent-brand-600 w-4 h-4"
            />
            Compare to previous period
          </label>
        </div>
      </div>

      {/* Custom date inputs */}
      {range === "custom" && (
        <div className="flex flex-wrap items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            From
            <input
              type="date"
              value={customFrom}
              max={customTo}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="ml-1 text-sm border border-gray-300 rounded-lg px-2 py-1 focus:ring-2 focus:ring-brand-500 focus:outline-none"
            />
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            To
            <input
              type="date"
              value={customTo}
              min={customFrom}
              max={todayStr()}
              onChange={(e) => setCustomTo(e.target.value)}
              className="ml-1 text-sm border border-gray-300 rounded-lg px-2 py-1 focus:ring-2 focus:ring-brand-500 focus:outline-none"
            />
          </label>
          <span className="text-xs text-gray-400">
            {days} day{days !== 1 ? "s" : ""}
          </span>
        </div>
      )}

      {/* ── KPI cards ──────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          label={`Scans (last ${days} days)`}
          value={scansInPeriod}
          delta={compare ? compBlock?.deltas?.scans_pct_change : null}
        />
        <StatCard
          label="Unique visitors"
          value={kpi.unique_ips_last_30_days}
          delta={compare ? compBlock?.deltas?.unique_pct_change : null}
        />
        <StatCard label="Active codes" value={kpi.active_codes_count} />
      </div>

      {/* ── Scans by day (with optional comparison line) ────────────────── */}
      <ScansByDayDualChart
        currSeries={currSeries}
        prevSeries={compare ? prevSeries : []}
        compare={compare}
      />

      {/* ── Device + Country ────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <DeviceDonut data={summary?.scans_by_device ?? []} />
        <CountryBars data={summary?.scans_by_country ?? []} />
      </div>

      {/* ── Hour heatmap ─────────────────────────────────────────────────── */}
      <HourHeatmap data={summary?.scans_by_hour ?? []} />

      {/* ── UTM breakdown ────────────────────────────────────────────────── */}
      <UTMSection utm={utm} />

      {/* ── Export (Pro+; sub-Pro sees an aspirational preview) ──────────── */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h2 className="text-sm font-semibold text-gray-700 mb-1">Export Data</h2>
        <p className="text-xs text-gray-500 mb-3">
          Download scan data for the selected date range as a CSV file.
        </p>
        {canExport ? (
          <ExportButton fromDate={fromDate} toDate={toDate} />
        ) : (
          <FeatureGate locked preview feature="Analytics export" tier="Pro">
            <ExportPreviewSample />
          </FeatureGate>
        )}
      </div>

      {/* ── Scheduled reports ────────────────────────────────────────────── */}
      <ScheduledReportSection />

    </div>
  );
}

export default AnalyticsPage;
